import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import API from '../../services/api';
import {
  BuildingOfficeIcon,
  UsersIcon,
  BanknotesIcon,
  ClockIcon,
  ArrowRightIcon,
  CurrencyEuroIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/solid';

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.4, ease: [0.16,1,0.3,1] } })
};

const DashboardAdmin = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/admin/dashboard')
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const stats = data?.stats || {};
  const recentPartners = data?.recent_partners || [];
  const recentTxns = data?.recent_transactions || [];
  const recentB2B = data?.recent_b2b || [];

  const iconStyle = { width: 18, height: 18, flexShrink: 0 };
  const kpis = [
    { icon: <BuildingOfficeIcon style={iconStyle} />, label: 'Partenaires actifs', value: stats.active_partners ?? '-', trend: `+${stats.pending_partners ?? 0} en attente`, color: '#3b82f6' },
    { icon: <UsersIcon style={iconStyle} />, label: 'Clients', value: stats.total_clients ?? '-', trend: '↑', color: '#10b981' },
    { icon: <BanknotesIcon style={iconStyle} />, label: 'Points en circulation', value: stats.points_in_circulation ? `${Math.round(stats.points_in_circulation / 1000)}k` : '-', trend: '↑', color: '#f59e0b' },
    { icon: <BanknotesIcon style={iconStyle} />, label: 'Revenu total', value: stats.total_revenue ? `${stats.total_revenue.toFixed(0)} TND` : '-', trend: '↑', color: '#8b5cf6' },
  ];

  const alertItems = [
    stats.pending_partners > 0 && { icon: <ClockIcon style={iconStyle} />, title: `${stats.pending_partners} partenaire(s) en attente`, desc: 'Demandes de validation', type: 'yellow', action: () => navigate('/dashboard/admin/partners') },
    stats.suspended_partners > 0 && { icon: <BanknotesIcon style={iconStyle} />, title: `${stats.suspended_partners} partenaire(s) suspendu(s)`, desc: 'Comptes inactifs', type: 'red', action: () => navigate('/dashboard/admin/partners') },
  ].filter(Boolean);

  const statusLabel = { active: 'Actif', pending: 'En attente', suspended: 'Suspendu' };
  const statusClass = { active: 'badge-active', pending: 'badge-pending', suspended: 'badge-suspended', completed: 'badge-active', failed: 'badge-suspended' };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#94a3b8', flexDirection: 'column', gap: 12 }}>
        <ArrowPathIcon style={{ width: 28, height: 28, color: '#3b82f6' }} className="icon-spin" />
        <span>Chargement des données...</span>
      </div>
    );
  }

  return (
    <div>
      {/* KPI Cards */}
      <div className="admin-kpi-grid">
        {kpis.map((kpi, i) => (
          <motion.div key={i} className="admin-kpi-card" custom={i} variants={cardVariants} initial="hidden" animate="visible">
            <div className="admin-kpi-header">
              <div className="admin-kpi-icon" style={{ background: `${kpi.color}18`, color: kpi.color }}>
                {kpi.icon}
              </div>
              <span className="admin-kpi-trend">{kpi.trend}</span>
            </div>
            <div className="admin-kpi-value">{kpi.value}</div>
            <div className="admin-kpi-label">{kpi.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Alerts */}
      {alertItems.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${alertItems.length}, 1fr)`, gap: 16, marginBottom: 24 }}>
          {alertItems.map((a, i) => (
            <motion.div
              key={i}
              onClick={a.action}
              style={{
                background: a.type === 'yellow' ? '#fffbeb' : '#fff5f5',
                borderLeft: `4px solid ${a.type === 'yellow' ? '#f59e0b' : '#ef4444'}`,
                borderRadius: 12, padding: 16,
                display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer'
              }}
              whileHover={{ scale: 1.01 }}
              custom={i + 4} variants={cardVariants} initial="hidden" animate="visible"
            >
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: a.type === 'yellow' ? '#fef3c7' : '#fee2e2',
                color: a.type === 'yellow' ? '#f59e0b' : '#ef4444',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {a.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: a.type === 'yellow' ? '#92400e' : '#991b1b', marginBottom: 2 }}>{a.title}</div>
                <div style={{ fontSize: 12, color: a.type === 'yellow' ? '#b45309' : '#b91c1c' }}>{a.desc}</div>
              </div>
              <ArrowRightIcon style={{ width: 18, height: 18, flexShrink: 0, color: a.type === 'yellow' ? '#f59e0b' : '#ef4444' }} />
            </motion.div>
          ))}
        </div>
      )}

      {/* Revenue split */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {[
          { label: 'Revenus B2B (achats packs)', value: stats.revenue_b2b, color: '#3b82f6' },
          { label: 'Revenus B2C (commissions)', value: stats.revenue_b2c, color: '#10b981' },
        ].map((item, i) => (
          <div key={i} className="admin-table-card" style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: `${item.color}18`, color: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CurrencyEuroIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>{item.value?.toFixed(2) ?? '0.00'} TND</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{item.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Partners */}
      <div className="admin-table-card">
        <div className="admin-table-card-header">
          <h3 style={{ color: '#3b82f6',fontWeight :'bold',fontSize:'18px' }}>Dernières inscriptions partenaires</h3>
          <button className="admin-view-all" onClick={() => navigate('/dashboard/admin/partners')}>Voir tout →</button>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Entreprise</th>
              <th>Email</th>
              <th>Pays</th>
              <th>Pack</th>
              <th>Statut</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {recentPartners.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>Aucun partenaire</td></tr>
            ) : recentPartners.map((p, i) => (
              <tr key={p.id || i}>
                <td><strong>{p.company_name}</strong></td>
                <td style={{ color: '#64748b' }}>{p.email}</td>
                <td>{p.country_code || '-'}</td>
                <td>{p.pack_name || <span style={{ color: '#94a3b8' }}>Aucun</span>}</td>
                <td><span className={statusClass[p.status] || 'badge-inactive'}>{statusLabel[p.status] || p.status}</span></td>
                <td style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(p.created_at).toLocaleDateString('fr')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recent B2C Transactions */}
      <div className="admin-table-card">
        <div className="admin-table-card-header">
          <h3 style={{ color: '#3b82f6',fontWeight :'bold',fontSize:'18px' }}>Transactions B2C récentes</h3>
          <button className="admin-view-all" onClick={() => navigate('/dashboard/admin/transactions')}>Voir tout →</button>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Partenaire</th>
              <th>Client</th>
              <th>Service</th>
              <th>Montant</th>
              <th>Points</th>
              <th>Statut</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {recentTxns.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>Aucune transaction</td></tr>
            ) : recentTxns.map((t, i) => (
              <tr key={t.id || i}>
                <td><code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>#{t.id}</code></td>
                <td>{t.partner_name}</td>
                <td style={{ color: '#64748b' }}>{t.client_name}</td>
                <td style={{ color: '#64748b' }}>{t.service_name || '-'}</td>
                <td><strong>{t.amount ? `${parseFloat(t.amount).toFixed(2)} TND` : '-'}</strong></td>
                <td>{t.points_amount?.toLocaleString('fr')} pts</td>
                <td><span className={statusClass[t.status] || 'badge-inactive'}>{t.status}</span></td>
                <td style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(t.created_at).toLocaleString('fr')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recent B2B */}
      <div className="admin-table-card">
        <div className="admin-table-card-header">
          <h3 style={{ color: '#3b82f6',fontWeight :'bold',fontSize:'18px' }}>Achats de packs B2B récents</h3>
          <button className="admin-view-all" onClick={() => navigate('/dashboard/admin/transactions')}>Voir tout →</button>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Partenaire</th>
              <th>Pack</th>
              <th>Points</th>
              <th>Montant payé</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {recentB2B.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>Aucun achat</td></tr>
            ) : recentB2B.map((b, i) => (
              <tr key={b.id || i}>
                <td><strong>{b.partner_name}</strong></td>
                <td>{b.pack_name || '-'}</td>
                <td>{b.points_amount?.toLocaleString('fr')} pts</td>
                <td><strong style={{ color: '#10b981' }}>{b.amount_paid ? `${parseFloat(b.amount_paid).toFixed(2)} TND` : '-'}</strong></td>
                <td style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(b.created_at).toLocaleString('fr')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DashboardAdmin;
